/**
 * Import de vault: fiecare element devine un credential nou, criptat în
 * browser exact ca la crearea din formular (DEK nou, wrap cu cheia publică a
 * userului). Serverul primește doar blob-ul criptat și atributele pe care
 * schema spațiului le declară non-secrete.
 */
import { ApiError, errorMessage } from '@/api/client';
import type { CredentialTypeSchemaResponse } from '@/api/credentialSchemas';
import { credentialsApi } from '@/api/credentials';
import { encryptCredential } from '@/crypto/sodium';
import type { TransferProgress } from '@/features/vault/transfer/exportVault';
import { duplicateKey, prepareCredential } from '@/features/vault/transfer/prepare';
import {
  TransferError,
  type SkippedEntry,
  type TransferCredential,
} from '@/features/vault/transfer/types';

export interface ImportReport {
  imported: number;
  skipped: SkippedEntry[];
  errors: Array<{ title: string; message: string }>;
  /** Elemente neatinse: import anulat sau oprit de un refuz al serverului. */
  remaining: number;
}

export interface ImportOptions {
  tenantId: string;
  publicKey: Uint8Array;
  schemasByType: Record<string, CredentialTypeSchemaResponse>;
  items: TransferCredential[];
  /** Sare peste elementele care există deja în spațiu (vezi `duplicateKey`). */
  skipDuplicates: boolean;
  onProgress?: (p: TransferProgress) => void;
  signal?: AbortSignal;
}

export async function importVault(opts: ImportOptions): Promise<ImportReport> {
  const report: ImportReport = { imported: 0, skipped: [], errors: [], remaining: 0 };
  const total = opts.items.length;

  const seen = new Set<string>();
  if (opts.skipDuplicates) {
    for (const existing of await credentialsApi.list(opts.tenantId)) {
      seen.add(duplicateKey(existing));
    }
  }

  for (const [index, item] of opts.items.entries()) {
    if (opts.signal?.aborted) {
      report.remaining = total - index;
      break;
    }
    opts.onProgress?.({ done: index, total, currentLabel: item.title });
    try {
      const { payload, ...fields } = prepareCredential(item, opts.schemasByType[item.type]);
      const key = duplicateKey(fields);
      if (opts.skipDuplicates && seen.has(key)) {
        report.skipped.push({ title: item.title, code: 'duplicate' });
        continue;
      }
      const encrypted = await encryptCredential(payload, opts.publicKey);
      await credentialsApi.create(opts.tenantId, { ...fields, ...encrypted });
      seen.add(key);
      report.imported++;
    } catch (err) {
      if (err instanceof TransferError) {
        report.skipped.push({ title: item.title, code: err.code, params: err.params });
        continue;
      }
      report.errors.push({ title: item.title, message: errorMessage(err) });
      // Sesiune expirată sau fără drept de scriere: restul ar eșua la fel.
      if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
        report.remaining = total - index - 1;
        break;
      }
    }
  }
  opts.onProgress?.({ done: total, total, currentLabel: '' });
  return report;
}
